import React, { ReactElement, ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { StackActions, useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { useLinkingStore } from '@/feature/entry-share/hooks/use-linking';
import { productNames } from '@/hooks/use-active-product-name';
import { useAvailableProducts } from '@/hooks/use-available-products';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { RootStackParamList } from '@/navigation/types';

import { ContactSupport } from './ContactSupport';

export const HiddenProductRedirectListener = ({ children }: { children: ReactNode }): ReactElement => {
    const unavailableProductRedirect = useLinkingStore(state => state.unavailableProductRedirect);
    const { availableProducts } = useAvailableProducts();
    const { showInfoSheet } = useAlerts();
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const { t } = useTranslation(['account', 'common']);

    useEffect(() => {
        if (!unavailableProductRedirect) {
            return;
        }

        const productName = productNames[unavailableProductRedirect];
        const activeProduct = useJurisdictionStore.getState().product;
        const defaultProductName = productNames[activeProduct];
        // Clear the flag to prevent re-triggering
        useLinkingStore.getState().setUnavailableProductRedirect(undefined);

        if (availableProducts.length === 0) {
            navigation.navigate('ErrorModal', {
                title: t('all_products_hidden_title'),
                subtitle: t('all_products_hidden_description'),
                primaryButton: t('review_product_visibility'),
                handlePress: () => {
                    // replace so it doesn't open as a sheet over the modal
                    navigation.dispatch(
                        StackActions.replace('AccountHome', {
                            screen: 'HideVerticals',
                            params: { navigateHomeOnBack: true },
                        })
                    );
                },
                closeIconMode: 'none',
                showSecondaryButton: false,
                footer: <ContactSupport />,
                showNavbarTitle: false,
            });
        } else {
            showInfoSheet({
                title: t('product_hidden_title', { product: productName }),
                description: t('product_hidden_description', {
                    product: productName,
                    default_product: defaultProductName,
                }),
                buttonLabel: t('continue_to_product', { default_product: defaultProductName }),
                secondaryLabel: t('review_product_visibility'),
                handleSecondaryPress: () => {
                    navigation.navigate('AccountHome', {
                        screen: 'HideVerticals',
                    });
                },
            });
        }
    }, [unavailableProductRedirect, availableProducts.length, showInfoSheet, navigation, t]);

    return <>{children}</>;
};
